import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAddCaseFormContext } from '../contexts/AddCaseFormContext';
import useDedicatedFileUpload from './useDedicatedFileUpload';
import useCaseRecordDelete from './useCaseRecordDelete';
import { buildEconomicDamagePaths } from '../utils/caseFieldPaths';
import { prepFileDataForDedicatedUpload, filterDuplicateFilesByName } from '../utils/helper';
import { RECORD_DATA_TYPE } from '@demandproai/shared';

const useEconomicDamageForm = () => {
    const { formik, selectedTiffIndex, getFieldProps } = useAddCaseFormContext();

    const { caseId } = useParams();
    const { handleFileRemove } = useCaseRecordDelete();

    const { isLoading: isMedicalLoading, dedicatedFileUpload: uploadFutureMedical, completeFileUpload: completeFutureMedical, startFileUpload: startFutureMedical } = useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.FUTURE);
    const { isLoading: isEarningLoading, dedicatedFileUpload: uploadLossOfEarning, completeFileUpload: completeLossOfEarning, startFileUpload: startLossOfEarning } = useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.INCOME_LOSS);
    const { isLoading: isCertifiedLoading, dedicatedFileUpload: uploadCeritifiedLifecare, completeFileUpload: completeCertifiedLifecare, startFileUpload: startCertifiedLifecare } = useDedicatedFileUpload(caseId, RECORD_DATA_TYPE.CERTIFIED_LIFECARE_PLAN);

    const formikValues = formik.values;
    const setFieldValue = formik.setFieldValue;
    const economicDamagePaths = buildEconomicDamagePaths({ selectedTiffIndex });
    const currentEconomicDamage = formikValues?.clients?.[selectedTiffIndex]?.damage || {};
    const lossOfEarningFiles = currentEconomicDamage?.lossOfEarning || [];
    const futureMedicalExpenseFiles = currentEconomicDamage?.futureMedicalExpense || [];

    const removeEconomicDamageFile = useCallback(async ({ fieldType, fileToRemove }) => {
        handleFileRemove({
            formik,
            fileToRemove,
            filePath: `clients[${selectedTiffIndex}].damage.${fieldType}`,
            removeData: { [economicDamagePaths[fieldType]]: { s3UrlPath: fileToRemove.s3UrlPath } }
        })
    }, [formik, selectedTiffIndex, economicDamagePaths, handleFileRemove]);

    const handleEconomicDamageUpload = useCallback(async ({ fieldType, files }) => {
        const prevFiles = formikValues?.clients?.[selectedTiffIndex]?.damage?.[fieldType] || [];
        
        const newFiles = Array.from(files.file);
        const uniqueNewFiles = filterDuplicateFilesByName({
            newFiles,
            prevFiles,
        });

        if (!uniqueNewFiles.length) return;
        const prepFiles = prepFileDataForDedicatedUpload(uniqueNewFiles);
        const fileCount = prepFiles.length;

        const uploadHandlersByFieldType = {
            lossOfEarning: {
                starter: startLossOfEarning,
                uploader: uploadLossOfEarning,
                completer: completeLossOfEarning
            },
            futureMedicalExpense: {
                starter: startFutureMedical,
                uploader: uploadFutureMedical,
                completer: completeFutureMedical
            },
            certifiedLifecarePlan: {
                starter: startCertifiedLifecare,
                uploader: uploadCeritifiedLifecare,
                completer: completeCertifiedLifecare
            }
        };
        const { starter, uploader, completer } = uploadHandlersByFieldType[fieldType] || {};
        if (!starter || !uploader || !completer) return;

        starter(fileCount);
        const dedicatedFiles = await uploader(prepFiles, {
            fieldKey: economicDamagePaths[fieldType],
            action: 'add'
        });

        const uniqueDedicatedFileArr = [...new Map([...prevFiles, ...dedicatedFiles].map(item => [item.name, item])).values()];
        setFieldValue(`clients[${selectedTiffIndex}].damage.${fieldType}`, uniqueDedicatedFileArr);

        completer(fileCount);
    }, [formikValues, selectedTiffIndex, setFieldValue, economicDamagePaths, startLossOfEarning, uploadLossOfEarning, completeLossOfEarning, startFutureMedical, uploadFutureMedical, completeFutureMedical, startCertifiedLifecare, uploadCeritifiedLifecare, completeCertifiedLifecare]);

    return {
        economicDamagePaths,
        getFieldProps,
        lossOfEarningFiles,
        futureMedicalExpenseFiles,
        isMedicalLoading,
        isEarningLoading,
        isCertifiedLoading,
        removeEconomicDamageFile,
        handleEconomicDamageUpload
    };
};

export default useEconomicDamageForm;
